import {
  Button,
  ButtonGroup,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "firebaseApp";
import { useDispatch, useSelector } from "react-redux";
import { createBotActions } from "store/createBot";
import { newBotModels, modelMeta } from "utils/modelUtils";
import TextInput from "../react-lib/generic-ui/TextInput";
import { useAuth } from "context/react-lib/firebase-auth/AuthProvider";
import ButtonWithConfirm from "components/react-lib/generic-ui/ButtonWithConfirm";
import Selector from "components/react-lib/generic-ui/Selector";
import AvatarSelector from "./AvatarSelector";
import { botActions } from "store/bot";
import LabelledOutline from "components/react-lib/generic-ui/LabelledOutline";
import { COLLECTIONS } from "firebaseCollections";
import AttachFileModal from "components/attachments/AttachFileModal";
import { modalsActions } from "store/react-lib/generic-ui/modals";
import Attachment from "components/attachments/Attachment";
import { colors } from "colors";
import AlertMessage from "components/react-lib/generic-ui/AlertMessage";
import { useCollection } from "react-firebase-hooks/firestore";
import { snapshotToArray } from "utils/react-lib/generic-ui/firebaseUtils";
import { useTheme } from "@emotion/react";
import ReduxModal from "components/react-lib/generic-ui/modals/ReduxModal";
import { MODALS } from "modals";
import CollectionSelector from "components/react-lib/generic-ui/CollectionSelector";
import ModelSelector from "components/model/ModelSelector";

export default function CreateBotModal() {
  const auth = useAuth();
  const theme = useTheme();
  const dispatch = useDispatch();
  const createBotState = useSelector((state) => state.createBot);
  const [processing, setProcessing] = useState(false);
  const [attachments, setAttachments] = useState([]);

  const [botAttachmentsSnap, loadingBotAttachments, botAttachmentsError] =
    useCollection(
      createBotState.id
        ? query(
            collection(db, "attachments"),
            where("targetId", "==", createBotState.id)
          )
        : null
    );

  if (botAttachmentsError) {
    console.log(botAttachmentsError);
  }

  const botAttachments = snapshotToArray(botAttachmentsSnap);

  const handleClose = () => {
    dispatch(modalsActions.closeModal(MODALS.createEditBotModal));
  };

  const uid = auth.currentUser?.uid;

  const updateFile = async (botId, fileId) => {
    if (!fileId) return;
    try {
      await updateDoc(doc(db, "files", fileId), {
        botId,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handleCreate = async () => {
    setProcessing(true);
    try {
      let botId = createBotState.id;
      if (createBotState.id) {
        await updateDoc(doc(db, COLLECTIONS.bots, createBotState.id), {
          name: createBotState.name,
          model: createBotState.model,
          systemPrompt: createBotState.systemPrompt || "You're a helpful assistant",
          avatar: createBotState.avatar,
          contextSize: createBotState.contextSize,
          maxTokens: createBotState.maxTokens || "",
          stream:
            createBotState.stream === undefined ? false : createBotState.stream,
          fileId: createBotState.fileId || "",
          isTool: createBotState.isTool || false,
          toolButtonLabel:
            createBotState.toolButtonLabel || createBotState.name,
        });
        await updateFile(createBotState.id, createBotState.fileId);
        dispatch(botActions.selectBot(createBotState.id));
      } else {
        const botRef = await addDoc(collection(db, COLLECTIONS.bots), {
          uid,
          name: createBotState.name,
          model: createBotState.model,
          systemPrompt: createBotState.systemPrompt || "You're a helpful assistant",
          avatar: createBotState.avatar,
          contextSize: createBotState.contextSize,
          maxTokens: createBotState.maxTokens || "",
          stream:
            createBotState.stream === undefined ? false : createBotState.stream,
          createdAt: new Date().getTime(),
          fileId: createBotState.fileId || "",
          isTool: createBotState.isTool || false,
          toolButtonLabel:
            createBotState.toolButtonLabel || createBotState.name,
        });
        botId = botRef.id;
        await updateFile(botRef.id, createBotState.fileId);
        dispatch(botActions.selectBot(botRef.id));
      }
      // Process new attachments
      for (const attachment of attachments) {
        const payload = {
          ...attachment,
          targetId: botId,
          createdAt: new Date().getTime(),
        };
        await addDoc(collection(db, "attachments"), payload);
      }
      setAttachments([]);
      handleClose();
    } catch (error) {
      console.log(error);
    } finally {
      setProcessing(false);
    }
  };

  const canCreate = () => {
    if (processing) return false;
    if (!uid) return false;
    if (!createBotState.name) return false;
    if (!createBotState.model) return false;
    //if (!createBotState.systemPrompt) return false;
    if (!createBotState.avatar) return false;
    if (createBotState.contextSize === "") return false;
    return true;
  };

  const handleDelete = async () => {
    try {
      await deleteDoc(doc(db, COLLECTIONS.bots, createBotState.id));
      handleClose();
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  const attachFileToBot = async ({ fileId, fileData }) => {
    if (
      [...attachments, ...botAttachments].filter(
        (attachment) => attachment.fileId === fileId
      ).length === 0
    ) {
      const file = {
        ...fileData,
        fileId,
      };
      setAttachments([...attachments, file]);
    }
    dispatch(modalsActions.closeModal(MODALS.attachFileToBotModal));
  };

  const handleRemove = (fileId) => {
    const newAttachments = attachments.filter(
      (attachment) => attachment.fileId !== fileId
    );
    setAttachments(newAttachments);
  };

  return (
    <ReduxModal
      fullWidth
      maxWidth="sm"
      name={MODALS.createEditBotModal}
      title={createBotState.id ? "Edit Bot" : "Create Bot"}
      dialogActions={
        <ButtonGroup fullWidth>
          <Button
            color="secondary"
            variant="contained"
            onClick={handleClose}
            disabled={processing}
            data-testid="bot-modal-close-button"
          >
            Cancel
          </Button>
          {createBotState.id && (
            <ButtonWithConfirm
              onClick={handleDelete}
              color="secondary"
              variant="contained"
              confirmTitle="Delete Bot"
              confirmMessage="Are you sure to delete this bot? This can't be undone."
            >
              Delete
            </ButtonWithConfirm>
          )}
          <Button
            data-testid="create-bot-button"
            variant="contained"
            disabled={!canCreate()}
            onClick={handleCreate}
          >
            {processing
              ? createBotState.id
                ? "Updaing"
                : "Creating"
              : createBotState.id
              ? "Update"
              : "Create"}
          </Button>
        </ButtonGroup>
      }
    >
      <AttachFileModal
        name={MODALS.attachFileToBotModal}
        successCallback={attachFileToBot}
      />
      <Stack py={1} spacing={2}>
        <TextInput
          color="secondary"
          inputDataTestId="bot-name-textinput"
          key="name-input"
          autoComplete="off"
          label="Name"
          value={createBotState.name}
          setValue={(name) =>
            dispatch(
              createBotActions.updateField({ field: "name", value: name })
            )
          }
        />
        <AvatarSelector
          avatar={createBotState.avatar}
          setAvatar={(avatar) =>
            dispatch(
              createBotActions.updateField({ field: "avatar", value: avatar })
            )
          }
        />
        <ModelSelector
          model={createBotState.model}
          setModel={(modelId) => (
            dispatch(
              createBotActions.updateField({ field: "model", value: modelId })
            )
          )}
        />
        <TextInput
          color="secondary"
          inputDataTestId="bot-prompt-textinput"
          fullWidth
          label="System prompt"
          helperText={`
            This prompt is used to let the bot know what kind of tasks you expect it to solve.
            Each time you talk with the bot, it will take it into account, together with
            the message. This makes it possible to specify a behavior you want the bot to
            have without having to specify each time you send a mesasge. 
            `}
          placeholder="You're a helpful assistant"
          value={createBotState.systemPrompt}
          setValue={(systemPrompt) =>
            dispatch(
              createBotActions.updateField({
                field: "systemPrompt",
                value: systemPrompt,
              })
            )
          }
          multiline
        />
        <LabelledOutline
          label="Bot files"
          rawLabelColor={theme.palette.formLabel.main}
        >
          <Stack alignItems="center" justifyContent="center">
            {(botAttachments.length > 0 || attachments.length > 0) && (
              <AlertMessage severity="warning">
                Bot attachments are used in every message sent to the bot. To
                prevent unnecessary spendings, make sure this file is required
                for the bot to function. Otherwise, consider attaching these to
                specific conversations.
              </AlertMessage>
            )}
            {botAttachments.map((attachment) => {
              return (
                <Attachment
                  key={attachment.id}
                  attachment={attachment}
                  color={theme.palette.text.main}
                />
              );
            })}
            {attachments.length > 0 && (
              <Typography variant="caption">New attachments</Typography>
            )}
            {attachments.map((attachment) => (
              <Attachment
                key={attachment.fileId}
                attachment={attachment}
                color={theme.palette.text.main}
                customRemove={() => handleRemove(attachment.fileId)}
              />
            ))}
            <Button
              variant="contained"
              color="secondary"
              onClick={() =>
                dispatch(modalsActions.openModal(MODALS.attachFileToBotModal))
              }
            >
              Add Context File
            </Button>
          </Stack>
        </LabelledOutline>
        <FormControl>
          <FormControlLabel
            control={
              <Checkbox
                color="secondary"
                checked={createBotState.isTool}
                onChange={() =>
                  dispatch(
                    createBotActions.updateField({
                      field: "isTool",
                      value: !createBotState.isTool,
                    })
                  )
                }
              />
            }
            label="Is Tool?"
          />
          <FormHelperText>
            Tools are use to transform text and are meant to be used in the{" "}
            <b>AI text editor</b>. If you set the bot as a tool, make sure the
            system instructions instruct the bot to perform a text
            transformation, for example:{" "}
            <i>"Fix the typos in the given text."</i> or{" "}
            <i>"Make the language more casual"</i>.
          </FormHelperText>
        </FormControl>
        {createBotState.isTool && (
          <TextField
            color="secondary"
            label="Tool button label for the AI text editor"
            value={createBotState.toolButtonLabel || createBotState.name}
            onChange={(event) =>
              dispatch(
                createBotActions.updateField({
                  field: "toolButtonLabel",
                  value: event.target.value,
                })
              )
            }
          />
        )}
        {!createBotState.isTool && (
          <TextInput
            color="secondary"
            label="Default context size"
            helperText={`
            The number of previous messages the bot takes into account when answering. By default, only the system prompt
            and the user message are considered.
          `}
            value={createBotState.contextSize}
            setValue={(contextSize) =>
              dispatch(
                createBotActions.updateField({
                  field: "contextSize",
                  value: contextSize,
                })
              )
            }
            type="number"
          />
        )}
      </Stack>
    </ReduxModal>
  );

}
